import React, {
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Center, Flex, Spinner, useBreakpointValue } from '@chakra-ui/react';
import { Navigate } from 'react-router-dom';

import { ErrorPage } from '@/components/ErrorPage';
import { Viewport } from '@/components/Viewport';
import { AccountNavBar, useGetCandidateProfileInfo } from '@/features/account';
import { UserType } from '@/services/api/codegen/api';

import { ADMIN_PATH } from '../admin';
import { useGetAccountQuery } from '../auth/useAuthQueries';

type AccountLayoutContextValue = {
  navDisplayed: boolean;
  setNavDisplayed: React.Dispatch<React.SetStateAction<boolean>>;
};

export const AccountLayoutContext =
  React.createContext<AccountLayoutContextValue | null>(null);

export const useAccountLayoutContext = () => {
  const ctx = useContext(AccountLayoutContext);
  if (ctx === null) {
    throw new Error('Missing parent <AccountLayout> component');
  }
  return ctx;
};

export const useAccountLayoutHideNav = (displayed: boolean = true) => {
  const { setNavDisplayed } = useAccountLayoutContext();

  useEffect(() => {
    setNavDisplayed(displayed);
    return () => setNavDisplayed(true);
  }, [setNavDisplayed, displayed]);
};

export const AccountLayout: FC<PropsWithChildren> = ({ children }) => {
  const [navDisplayed, setNavDisplayed] = useState<boolean>(true);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const { isLoading, error } = useGetCandidateProfileInfo();
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery();

  const providerValue = useMemo(
    () =>
      ({
        navDisplayed,
        setNavDisplayed,
      }) satisfies AccountLayoutContextValue,
    [navDisplayed]
  );

  if (isLoading || isAccountLoading) {
    return (
      <Center bgGradient="linear(to-b, brand.25, gray.50)" h="100vh" flex={1}>
        <Spinner />
      </Center>
    );
  }

  if (account?.userType === UserType.CompanyAdmin) {
    return <Navigate to={`${ADMIN_PATH}/dashboard`} replace />;
  }

  if (error) {
    return <ErrorPage errorCode={error.status} />;
  }

  return (
    <AccountLayoutContext.Provider value={providerValue}>
      <Viewport bgGradient="linear(to-b, brand.25, gray.50)">
        {!!navDisplayed && !isMobile && <AccountNavBar display="block" />}
        <Flex flex="1" direction="column">
          {children}
        </Flex>
      </Viewport>
    </AccountLayoutContext.Provider>
  );
};
